// 고정줄이 쓸 시계 한 벌
//
// 화면마다 이 배선을 다시 하지 않으려고 훅으로 뺐다. 계측 프로브가 먼저 이 배선을
// 손으로 했고, 접속 스텝에서 화면이 둘 더 생기면서 세 번째 사본이 될 참이었다.
//
// **값을 상태에 쌓지 않고 매 틱마다 시계에서 다시 읽는다.** 쌓아 두면 그 값이 언제
// 찍힌 것인지가 화면에서 사라지고, 늦게 그려진 숫자를 사람이 편차로 읽는다.

import { useEffect, useState } from 'react'

import { toMillis } from '../../core/time/brand.ts'
import { systemClock, type Clock } from '../../core/time/clock.ts'
import { ClockSync, httpTransport, type SkewView } from '../clock/sync.ts'

export interface ShellClock {
  /** 서버 시각으로 보정한 지금. 아직 수렴 전이면 기기 시계 그대로다. */
  utcMillis: number
  /**
   * 고정줄의 편차 칸이 그릴 것 한 벌. **동기화기가 통째로 만들어 준다.**
   *
   * 전에는 밀리초와 진행 표시를 따로 내보내고 화면이 「null 이면 측정 중」이라는 규칙을
   * 스스로 세웠는데, 그 규칙을 쓰는 화면이 다섯이라 한 곳이 반드시 뒤처진다. 실제로 계측
   * 프로브가 이 훅 밖에서 같은 규칙을 손으로 다시 짜고 있었다.
   */
  skew: SkewView
}

export interface UseShellClockOptions {
  clock?: Clock
  sync?: ClockSync
  /** 화면을 다시 그리는 간격. */
  tickMs?: number
  /** 아직 표본을 다 못 모았을 때의 왕복 간격. */
  warmupGapMs?: number
  /** 다 모은 뒤의 왕복 간격. */
  steadyGapMs?: number
}

/**
 * 느린 예약을 기다리는 왕복을 당길 것인가 (배포 전 보수 회차, 2026-10-07).
 *
 * 다음 왕복의 예약은 직전 왕복이 끝날 때 정해지고, 표본이 다 차 있으면 느린 간격이다. 그 사이에 읽는 쪽이 시계
 * 점프를 보고 창을 비워도 예약은 그대로라 「측정 중 (0/3)」이 최대 그 간격만큼 머물렀다(대리 관측, 루프백에서
 * 0.3~5.3초). 그동안 카운트다운은 기기 시계로 흐른다. 아래 재수렴 묶음과 같은 자물쇠(직전 왕복의 성공)를 건다.
 */
export function shouldHurryProbe(state: {
  waitingSteady: boolean
  probing: boolean
  lastOk: boolean
  heldSamples: number
  minSamples: number
}): boolean {
  return state.waitingSteady && !state.probing && state.lastOk && state.heldSamples < state.minSamples
}

export function useShellClock(options: UseShellClockOptions = {}): ShellClock {
  const [clock] = useState<Clock>(() => options.clock ?? systemClock())
  const [sync] = useState<ClockSync>(
    () => options.sync ?? new ClockSync({ clock, transport: httpTransport() }),
  )
  const [, forceRender] = useState(0)

  useEffect(() => {
    let alive = true
    let probeTimer: ReturnType<typeof setTimeout> | undefined
    const warmupGapMs = options.warmupGapMs ?? 150
    const steadyGapMs = options.steadyGapMs ?? 5_000

    const probe = async (): Promise<boolean> => {
      let ok = true
      try {
        await sync.probe()
      } catch {
        // 표본 하나를 놓친 것뿐이다. 화면은 그동안 「측정 중」을 띄우고 있고, 그것이
        // 틀린 숫자를 자신 있게 보여 주는 것보다 낫다.
        ok = false
      }
      if (alive) forceRender((n) => n + 1)
      return ok
    }

    /**
     * **다시 모으는 동안은 처음 모을 때와 같은 속도로 간다.**
     *
     * 시계가 뛰면 창이 통째로 비고 표본 셋을 다시 모아야 하는데, 5초 폴링으로는 그 일에
     * 14초가 걸린다. 2026-09-01 실측에서 이 머신의 벽시계가 30초마다 1.9초씩 뛰었고, 그때마다
     * 화면에서 밀리초 숫자가 14초씩 사라져 180초 중 41.7%가 빈 채로 남았다. 값을 감추는 판단
     * 자체는 옳다 (1.9초는 표시 오차 예산 250밀리초의 여덟 배다). 옳지 않은 것은 **다시 아는 데
     * 걸리는 시간**이고, 그것은 안전 성질을 하나도 안 무르고 줄일 수 있다.
     *
     * **자물쇠는 「직전 왕복이 성공했는가」다.** 실패했으면 느린 쪽으로 간다. 그래서 빠른
     * 경로는 성공한 왕복만 타고, 재수렴 한 번에 최대 `minSamples` 회로 저절로 묶인다. 이것이
     * 없으면 서버가 죽어 있는 동안 150밀리초마다 두드리게 된다.
     *
     * 마운트 때의 묶음과 재수렴 때의 묶음을 **같은 기전 하나로** 쓴다. 「아직 못 모았다」는
     * 조건이 둘 다에서 참이라, 특수 케이스를 더하는 것이 아니라 오히려 하나를 없앤다.
     */
    const nextGapMs = (lastOk: boolean): number => {
      if (!lastOk) return steadyGapMs
      return sync.heldSamples < sync.minSamples ? warmupGapMs : steadyGapMs
    }

    // 지금 왕복 중인가, 직전 왕복이 성공했는가, 잡혀 있는 예약이 느린 간격인가. 그리기 타이머가 당길지 정한다.
    let probing = false
    let lastOk = false
    let waitingSteady = false

    const loop = async (): Promise<void> => {
      if (probeTimer !== undefined) clearTimeout(probeTimer)
      probing = true
      waitingSteady = false
      const ok = await probe()
      probing = false
      lastOk = ok
      if (!alive) return
      const gap = nextGapMs(ok)
      waitingSteady = gap === steadyGapMs
      probeTimer = setTimeout(() => void loop(), gap)
    }
    void loop()

    const drawTimer = setInterval(() => {
      forceRender((n) => n + 1)
      // 읽는 쪽이 점프를 보고 창을 비웠으면 느린 예약을 기다리지 않는다.
      if (
        shouldHurryProbe({
          waitingSteady,
          probing,
          lastOk,
          heldSamples: sync.heldSamples,
          minSamples: sync.minSamples,
        })
      ) {
        void loop()
      }
    }, options.tickMs ?? 200)

    // 화면이 돌아올 때마다 두 축을 견준다. 기기가 자는 동안 서버와의 관계가 달라졌을
    // 수 있는데, 낡은 표본을 섞으면 틀린 값을 자신 있게 표시하게 된다.
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return
      sync.checkContinuity()
      // 예약을 지우고 곧바로 다시 시작한다. 그러지 않으면 깨어난 직후의 재수렴이 남아
      // 있던 5초 예약을 기다리게 되고, 위의 빠른 묶음이 그 5초만큼 늦게 시작한다.
      if (probeTimer !== undefined) clearTimeout(probeTimer)
      void loop()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      alive = false
      if (probeTimer !== undefined) clearTimeout(probeTimer)
      clearInterval(drawTimer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [sync, options.tickMs, options.warmupGapMs, options.steadyGapMs])

  const serverNow = sync.serverNow()

  return {
    utcMillis: serverNow === null ? toMillis(clock.now()) : toMillis(serverNow),
    skew: sync.skewView(),
  }
}
